import { useCallback, useEffect, useRef, useState } from "react";
import { DeviceLicensesPanel } from "@/components/billing/DeviceLicensesPanel";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { functionTabShellStyle, tabLabelStyle } from "@/components/common/tab-shell";
import { TextInputField } from "@/components/common/TextInputField";
import {
	createCreditGrant,
	type CreditBalance,
	type CreditExpiration,
	type CreditGrant,
} from "@/lib/api";
import { CREDIT_EXPIRY_DAYS, formatCredits } from "@/lib/billing";
import { formatDateTime } from "@/lib/format-datetime";

type Tab = "credits" | "deviceLicenses";

const TABS: readonly { id: Tab; label: string }[] = [
	{ id: "credits", label: "クレジット" },
	{ id: "deviceLicenses", label: "デバイスのライセンス" },
];

/**
 * 超管理者の「付与と履歴」のダイアログ。クレジットのタブでは、外部での請求・入金の完了後に
 * 手動で付与を計上する。負の額は過去の付与の修正・取消を表す (履歴は書き換えない)。
 * 残高・履歴は親 (課金ダッシュボード) が取得したものを受け取り、付与の成功時は
 * onGranted で親に再取得させる。失効は DB に行を持たないが、履歴には付与と並べて表示する。
 * デバイスのライセンスのタブは DeviceLicensesPanel が持つ。
 */
export function CreditGrantsDialog({
	open,
	onClose,
	organizationId,
	organizationName,
	balance,
	onGranted,
}: {
	open: boolean;
	onClose: () => void;
	organizationId: string;
	organizationName: string;
	balance: CreditBalance;
	onGranted: () => void;
}) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const history = buildHistory(balance);
	const [amount, setAmount] = useState("");
	const [reason, setReason] = useState("");
	const [invoiceNumber, setInvoiceNumber] = useState("");
	const [amountError, setAmountError] = useState<string | null>(null);
	const [reasonError, setReasonError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [tab, setTab] = useState<Tab>("credits");

	// 開くのは親の open に従い、閉じるのは常に <dialog> 自身の close() に任せる (Esc の
	// 既定動作も close() を通る)。showModal() 中は top layer に置かれるため、close() を
	// 経ずに React が要素ごと DOM から外すと top layer の解体がブラウザ任せになり、
	// open のまま背景要素の下に取り残されることがある。
	useEffect(() => {
		const el = dialogRef.current;
		if (open && el && !el.open) el.showModal();
	}, [open]);

	useEffect(() => {
		if (open) {
			setAmount("");
			setReason("");
			setInvoiceNumber("");
			setAmountError(null);
			setReasonError(null);
			setSubmitError(null);
			setLoading(false);
			setTab("credits");
		}
	}, [open]);

	// Esc も閉じるボタンも close() の一本道に寄せる。親の状態を直接倒して要素ごと
	// アンマウントすると、close() を経ないまま top layer から外れる。
	const close = useCallback(() => dialogRef.current?.close(), []);

	function validate(): number | null {
		let ok = true;
		const parsed = Number(amount.trim());
		if (amount.trim() === "" || !Number.isFinite(parsed)) {
			setAmountError("付与額は数値で入力してください。");
			ok = false;
		} else if (parsed === 0) {
			setAmountError("付与額には 0 以外を指定してください。");
			ok = false;
		} else {
			setAmountError(null);
		}
		if (!reason.trim()) {
			setReasonError("理由は必須です。");
			ok = false;
		} else {
			setReasonError(null);
		}
		return ok ? parsed : null;
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		const parsed = validate();
		if (parsed === null) return;
		setSubmitError(null);
		setLoading(true);
		try {
			await createCreditGrant({
				organizationId,
				amountCredits: parsed,
				reason: reason.trim(),
				invoiceNumber: invoiceNumber.trim() || undefined,
			});
			setAmount("");
			setReason("");
			setInvoiceNumber("");
			onGranted();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			data-testid="credit-grants-dialog"
			onClose={onClose}
			onKeyDown={(e) => {
				// 中の確認ダイアログが処理した Escape では閉じない。
				if (e.key === "Escape" && !e.defaultPrevented) close();
			}}
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 840,
				maxWidth: "calc(100vw - 32px)",
				// 履歴の件数でダイアログが伸び縮みしないよう高さを固定し、履歴側だけスクロールさせる。
				height: "min(620px, calc(100vh - 32px))",
				display: "flex",
				flexDirection: "column",
			}}
			className="backdrop:bg-scrim"
		>
			<div
				style={{
					padding: "20px 24px 16px",
					borderBottom: "var(--pane-header-border)",
					flexShrink: 0,
				}}
			>
				<h3
					style={{
						margin: 0,
						fontSize: "var(--text-title)",
						fontWeight: 600,
						color: "var(--fg-1)",
						fontFamily: "var(--font-sans)",
					}}
				>
					付与と履歴
				</h3>
				<p
					style={{
						margin: "6px 0 0",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-normal)",
						color: "var(--fg-3)",
						display: "grid",
					}}
				>
					{/* タブを切り替えてもヘッダーの高さが変わらないよう、両方の説明を重ねて高いほうに揃える。 */}
					<span
						style={{
							gridArea: "1 / 1",
							visibility: tab === "credits" ? "visible" : "hidden",
						}}
					>
						{organizationName}{" "}
						への付与です。入金の確認後に計上してください。負の額は過去の付与の修正・取消を表します。
						付与の未使用分は {CREDIT_EXPIRY_DAYS} 日後に失効します (消費は古い付与から充当)。
					</span>
					<span
						style={{
							gridArea: "1 / 1",
							visibility: tab === "credits" ? "hidden" : "visible",
						}}
					>
						{organizationName}{" "}
						のデバイスのライセンスです。解約したライセンスは次の更新日に終わります
						(返金はありません)。
					</span>
				</p>
			</div>

			<div
				style={{
					display: "flex",
					height: 36,
					padding: "0 12px",
					borderBottom: "var(--pane-header-border)",
					flexShrink: 0,
				}}
			>
				{TABS.map(({ id, label }) => (
					<button
						key={id}
						type="button"
						data-testid={`credit-grants-tab-${id}`}
						onClick={() => setTab(id)}
						style={{ ...functionTabShellStyle(tab === id), padding: "0 12px" }}
					>
						<span style={tabLabelStyle}>{label}</span>
					</button>
				))}
			</div>

			{tab === "deviceLicenses" && (
				<DeviceLicensesPanel organizationId={organizationId} onGranted={onGranted} />
			)}

			{/* 左に付与フォーム、右に履歴の 2 カラム (row-reverse で入れ替え)。履歴は右カラム内でだけスクロールする。 */}
			<div
				style={{
					display: tab === "credits" ? "flex" : "none",
					flexDirection: "row-reverse",
					alignItems: "stretch",
					flex: 1,
					minHeight: 0,
				}}
			>
				{/* 長い行は折り返し、カラム内の縦スクロールだけで見せる (ダイアログの寸法は固定)。 */}
				<div
					style={{
						flex: 1,
						minWidth: 0,
						padding: "16px 24px",
						borderLeft: "var(--pane-header-border)",
						overflowY: "auto",
					}}
				>
					<h4
						style={{
							margin: "0 0 8px",
							fontSize: "var(--text-caption)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-heading)",
							color: "var(--fg-2)",
						}}
					>
						付与履歴
					</h4>
					{history.length === 0 && (
						<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
							付与はまだありません
						</p>
					)}
					{/* 日時・額・理由・付与者の 4 列。各行は subgrid で列幅を共有し、行ごとにずれないようにする。 */}
					{history.length > 0 && (
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "auto auto minmax(0, 1fr) auto",
								columnGap: 12,
								rowGap: 8,
								alignItems: "baseline",
								fontSize: "var(--text-caption)",
							}}
						>
							{history.map((entry) =>
								entry.kind === "grant" ? (
									<GrantRow key={entry.grant.id} grant={entry.grant} />
								) : (
									<ExpirationRow
										key={`expiration-${entry.expiration.grantId}`}
										expiration={entry.expiration}
										grant={entry.grant}
									/>
								),
							)}
						</div>
					)}
				</div>

				<form onSubmit={handleSubmit} noValidate style={{ width: 320, flexShrink: 0 }}>
					<div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
						{submitError && (
							<div
								data-testid="credit-grant-submit-error"
								style={{
									padding: "10px 14px",
									borderRadius: "var(--radius-3)",
									background: "var(--danger-subtle)",
									boxShadow: "0 0 0 1px var(--danger-line)",
									fontSize: "var(--text-ui)",
									color: "var(--danger)",
									fontFamily: "var(--font-sans)",
								}}
							>
								{submitError}
							</div>
						)}
						<TextInputField
							label="付与額 (クレジット)"
							value={amount}
							onChange={setAmount}
							placeholder="100000"
							autoComplete="off"
							error={amountError ?? undefined}
						/>
						<TextInputField
							label="理由"
							value={reason}
							onChange={setReason}
							autoComplete="off"
							error={reasonError ?? undefined}
						/>
						<TextInputField
							label="請求書番号 (任意)"
							value={invoiceNumber}
							onChange={setInvoiceNumber}
							autoComplete="off"
						/>
						<div style={{ display: "flex", justifyContent: "flex-end" }}>
							<ConfirmButton type="submit" label="付与" loading={loading} />
						</div>
					</div>
				</form>
			</div>

			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					padding: "12px 24px",
					borderTop: "var(--pane-header-border)",
					flexShrink: 0,
				}}
			>
				<ConfirmButton
					label="閉じる"
					variant="ghost"
					loading={false}
					onClick={close}
					data-testid="credit-grants-close"
				/>
			</div>
		</dialog>
	);
}

type HistoryEntry =
	| { kind: "grant"; at: string; grant: CreditGrant }
	| {
			kind: "expiration";
			at: string;
			expiration: CreditExpiration;
			grant: CreditGrant | undefined;
	  };

/** 付与と失効を 1 本の履歴 (新しい順) にまとめる。 */
function buildHistory(balance: CreditBalance): HistoryEntry[] {
	const grantsById = new Map(balance.grants.map((g) => [g.id, g]));
	const entries: HistoryEntry[] = [
		...balance.grants.map((grant) => ({ kind: "grant" as const, at: grant.createdAt, grant })),
		...balance.expirations.map((expiration) => ({
			kind: "expiration" as const,
			at: expiration.expiredAt,
			expiration,
			grant: grantsById.get(expiration.grantId),
		})),
	];
	return entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

const rowStyle: React.CSSProperties = {
	display: "grid",
	gridColumn: "1 / -1",
	gridTemplateColumns: "subgrid",
	alignItems: "baseline",
};

const amountStyle: React.CSSProperties = {
	textAlign: "right",
	fontWeight: 600,
	fontVariantNumeric: "tabular-nums",
};

function GrantRow({ grant }: { grant: CreditGrant }) {
	return (
		<div data-testid={`credit-grant-row-${grant.id}`} style={rowStyle}>
			<span style={{ color: "var(--fg-3)" }}>{formatDateTime(grant.createdAt)}</span>
			<span
				style={{
					...amountStyle,
					color: grant.amountCredits < 0 ? "var(--danger)" : "var(--fg-1)",
				}}
			>
				{grant.amountCredits > 0 ? "+" : ""}
				{formatCredits(grant.amountCredits)}
			</span>
			<span style={{ color: "var(--fg-2)", overflowWrap: "anywhere" }}>
				{grant.reason}
				{grant.invoiceNumber !== null && (
					<span style={{ color: "var(--fg-3)" }}> ({grant.invoiceNumber})</span>
				)}
			</span>
			<span style={{ color: "var(--fg-3)" }}>{grant.grantedByUserName}</span>
		</div>
	);
}

function ExpirationRow({
	expiration,
	grant,
}: {
	expiration: CreditExpiration;
	grant: CreditGrant | undefined;
}) {
	return (
		<div data-testid={`credit-expiration-row-${expiration.grantId}`} style={rowStyle}>
			<span style={{ color: "var(--fg-3)" }}>{formatDateTime(expiration.expiredAt)}</span>
			<span style={{ ...amountStyle, color: "var(--fg-3)" }}>
				−{formatCredits(expiration.amountCredits)}
			</span>
			<span style={{ color: "var(--fg-2)", overflowWrap: "anywhere" }}>
				失効
				{grant && (
					<span style={{ color: "var(--fg-3)" }}>
						{" "}
						({formatDateTime(grant.createdAt)}の付与 +{formatCredits(grant.amountCredits)}
						の未使用分)
					</span>
				)}
			</span>
		</div>
	);
}
